import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

// Cloudflare Turnstile bot check for the public forms (registration, login and forgot password, mobile email reminder).
// The script loads only when a form that needs it is on screen, unlike the old reCAPTCHA provider,
// which wrapped the whole app and kept running in the background on every page.
// Site key: REACT_APP_TURNSTILE_SITE_KEY. Locally use Cloudflare's test key 1x00000000000000000000AA
// (always passes). Without a key the widget renders nothing and the form sends no token; the backend
// then skips the check only if its TURNSTILE_SECRET_KEY is unset too (see hsrc-api authentication/turnstile.py).
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
export const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

let scriptPromise = null;
const loadScript = () => {
	if (window.turnstile) return Promise.resolve(window.turnstile);
	if (!scriptPromise) {
		scriptPromise = new Promise((resolve, reject) => {
			const script = document.createElement("script");
			script.src = SCRIPT_URL;
			script.async = true;
			script.onload = () => resolve(window.turnstile);
			script.onerror = () => {
				scriptPromise = null;
				reject(new Error("Turnstile script failed to load"));
			};
			document.head.appendChild(script);
		});
	}
	return scriptPromise;
};

// onToken(token) receives a fresh token, or null when it expires or the check fails.
// The parent can call ref.current.reset() after a submit: a token is single-use and valid for 5 minutes.
const TurnstileWidget = forwardRef(({ action, onToken }, ref) => {
	const containerRef = useRef(null);
	const widgetIdRef = useRef(null);
	const onTokenRef = useRef(onToken);
	onTokenRef.current = onToken;

	useImperativeHandle(ref, () => ({
		reset: () => {
			onTokenRef.current(null);
			if (window.turnstile && widgetIdRef.current !== null) window.turnstile.reset(widgetIdRef.current);
		},
	}));

	useEffect(() => {
		if (!turnstileSiteKey) return undefined;
		let cancelled = false;
		loadScript()
			.then((turnstile) => {
				if (cancelled || !containerRef.current) return;
				widgetIdRef.current = turnstile.render(containerRef.current, {
					sitekey: turnstileSiteKey,
					action,
					callback: (token) => onTokenRef.current(token),
					"expired-callback": () => onTokenRef.current(null),
					"error-callback": () => onTokenRef.current(null),
				});
			})
			.catch((error) => {
				console.warn(error.message);
				onTokenRef.current(null);
			});
		return () => {
			cancelled = true;
			if (window.turnstile && widgetIdRef.current !== null) window.turnstile.remove(widgetIdRef.current);
			widgetIdRef.current = null;
		};
	}, [action]);

	if (!turnstileSiteKey) return null;
	return <div ref={containerRef} className='flex justify-center my-3' />;
});

export default TurnstileWidget;
